/**
 * Admin Announcements Management
 * Create and manage system announcements
 */

import React, { useState, useEffect } from 'react';
import {
  Megaphone,
  Plus,
  Edit,
  Trash2,
  Eye,
  Pin,
  Archive,
  Send,
  Calendar,
  TrendingUp,
} from 'lucide-react';

interface Announcement {
  id: number;
  title: string;
  content: string;
  category: string | null;
  status: 'draft' | 'scheduled' | 'published' | 'archived';
  is_pinned: number;
  published_at: string | null;
  scheduled_for: string | null;
  views: number;
  created_at: string;
  updated_at: string;
}

interface Statistics {
  total: number;
  published: number;
  draft: number;
  scheduled: number;
  pinned: number;
  totalViews: number;
}

export const Announcements: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('');

  useEffect(() => {
    loadAnnouncements();
    loadStatistics();
  }, [filterStatus, filterCategory]);

  const loadAnnouncements = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterCategory) params.append('category', filterCategory);

      const response = await fetch(`/api/v1/admin/announcements?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setAnnouncements(data.data.announcements);
      }
    } catch (error) {
      console.error('Failed to load announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await fetch('/api/v1/admin/announcements/statistics', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setStatistics(data.data.statistics);
      }
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  };

  const handlePublish = async (announcementId: number) => {
    if (!confirm('Publish this announcement now?')) return;

    try {
      const response = await fetch(
        `/api/v1/admin/announcements/${announcementId}/publish`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        loadAnnouncements();
        loadStatistics();
      }
    } catch (error) {
      console.error('Failed to publish announcement:', error);
    }
  };

  const handleArchive = async (announcementId: number) => {
    if (!confirm('Archive this announcement?')) return;

    try {
      const response = await fetch(
        `/api/v1/admin/announcements/${announcementId}/archive`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        loadAnnouncements();
        loadStatistics();
      }
    } catch (error) {
      console.error('Failed to archive announcement:', error);
    }
  };

  const handleDelete = async (announcementId: number) => {
    if (!confirm('Are you sure you want to delete this announcement?')) return;

    try {
      const response = await fetch(`/api/v1/admin/announcements/${announcementId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        loadAnnouncements();
        loadStatistics();
      }
    } catch (error) {
      console.error('Failed to delete announcement:', error);
    }
  };

  const getCategoryBadge = (category: string | null) => {
    const colors = {
      general: 'bg-orange-900 text-blue-300',
      maintenance: 'bg-yellow-900 text-yellow-300',
      feature: 'bg-green-900 text-green-300',
      security: 'bg-red-900 text-red-300',
    };

    return colors[category as keyof typeof colors] || 'bg-slate-700/50 text-slate-300';
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      published: 'bg-green-900 text-green-300',
      draft: 'bg-slate-700/50 text-slate-300',
      scheduled: 'bg-orange-900 text-blue-300',
      archived: 'bg-gray-600 text-slate-400',
    };

    return colors[status as keyof typeof colors] || 'bg-slate-700/50 text-slate-300';
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Megaphone className="w-6 h-6" />
              Announcements
            </h1>
            <p className="text-slate-400 mt-1">
              Manage system-wide announcements
            </p>
          </div>
          <button className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-2">
            <Plus className="w-4 h-4" />
            New Announcement
          </button>
        </div>
      </div>

      {/* Statistics */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-blue-100 text-sm">Total</span>
              <Megaphone className="w-5 h-5 text-blue-100" />
            </div>
            <div className="text-2xl font-bold text-white">{statistics.total}</div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-green-100 text-sm">Published</span>
              <Send className="w-5 h-5 text-green-100" />
            </div>
            <div className="text-2xl font-bold text-white">{statistics.published}</div>
          </div>

          <div className="bg-gradient-to-br from-gray-500 to-gray-600 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-100 text-sm">Drafts</span>
              <Edit className="w-5 h-5 text-gray-100" />
            </div>
            <div className="text-2xl font-bold text-white">{statistics.draft}</div>
          </div>

          <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-indigo-100 text-sm">Scheduled</span>
              <Calendar className="w-5 h-5 text-indigo-100" />
            </div>
            <div className="text-2xl font-bold text-white">{statistics.scheduled}</div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-purple-100 text-sm">Pinned</span>
              <Pin className="w-5 h-5 text-purple-100" />
            </div>
            <div className="text-2xl font-bold text-white">{statistics.pinned}</div>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-orange-100 text-sm">Total Views</span>
              <TrendingUp className="w-5 h-5 text-orange-100" />
            </div>
            <div className="text-2xl font-bold text-white">
              {statistics.totalViews.toLocaleString()}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 mb-6">
        <div className="flex gap-4">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white"
          >
            <option value="">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
            <option value="archived">Archived</option>
          </select>

          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white"
          >
            <option value="">All Categories</option>
            <option value="general">General</option>
            <option value="maintenance">Maintenance</option>
            <option value="feature">Feature</option>
            <option value="security">Security</option>
          </select>
        </div>
      </div>

      {/* Announcements Table */}
      <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-700/50">
            <tr>
              <th className="text-left p-4 text-slate-300 font-medium">Title</th>
              <th className="text-left p-4 text-slate-300 font-medium">Category</th>
              <th className="text-left p-4 text-slate-300 font-medium">Status</th>
              <th className="text-left p-4 text-slate-300 font-medium">Published</th>
              <th className="text-left p-4 text-slate-300 font-medium">Views</th>
              <th className="text-right p-4 text-slate-300 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/50">
            {loading ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-slate-400">
                  Loading...
                </td>
              </tr>
            ) : announcements.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-slate-400">
                  No announcements found
                </td>
              </tr>
            ) : (
              announcements.map((announcement) => (
                <tr key={announcement.id} className="hover:bg-slate-700/30">
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {announcement.is_pinned === 1 && (
                        <Pin className="w-4 h-4 text-purple-400" />
                      )}
                      <div>
                        <div className="text-white font-medium">
                          {announcement.title}
                        </div>
                        <div className="text-sm text-slate-400 line-clamp-1">
                          {announcement.content.replace(/<[^>]*>/g, '').substring(0, 60)}...
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    {announcement.category && (
                      <span
                        className={`px-2 py-1 rounded text-sm ${getCategoryBadge(
                          announcement.category
                        )}`}
                      >
                        {announcement.category}
                      </span>
                    )}
                  </td>
                  <td className="p-4">
                    <span
                      className={`px-2 py-1 rounded text-sm ${getStatusBadge(
                        announcement.status
                      )}`}
                    >
                      {announcement.status}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="text-slate-300 text-sm">
                      {announcement.published_at
                        ? new Date(announcement.published_at).toLocaleDateString()
                        : announcement.scheduled_for
                        ? `Scheduled: ${new Date(announcement.scheduled_for).toLocaleDateString()}`
                        : '-'}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-1 text-slate-300">
                      <Eye className="w-4 h-4" />
                      {announcement.views}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      {announcement.status === 'draft' && (
                        <button
                          onClick={() => handlePublish(announcement.id)}
                          className="p-2 text-green-400 hover:bg-slate-700/50 rounded"
                          title="Publish"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        className="p-2 text-orange-400 hover:bg-slate-700/50 rounded"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {announcement.status === 'published' && (
                        <button
                          onClick={() => handleArchive(announcement.id)}
                          className="p-2 text-yellow-400 hover:bg-slate-700/50 rounded"
                          title="Archive"
                        >
                          <Archive className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(announcement.id)}
                        className="p-2 text-red-400 hover:bg-slate-700/50 rounded"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Announcements;
